import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  VStack,
  HStack,
  Alert,
  AlertIcon,
  useToast,
  FormControl,
  FormLabel,
  Select,
  Text,
  Flex,
  Button,
  Spinner,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Divider,
  SimpleGrid,
} from "@chakra-ui/react";
import { ChevronRightIcon, RepeatIcon } from "@chakra-ui/icons";
import { useAdminData } from "../../hooks/useAdminData";
import { createToast } from "../../utils/toastUtils";
import { formatDate } from "../../utils/dateUtils";
import { Api } from "../../Api";
import Pagination from "../shared/Pagination";
import InlineAudioPlayer from "../shared/InlineAudioPlayer";
import { usePersistentState } from "../../hooks/usePersistentState";
import { useSearchParams } from "react-router-dom";

const ITEMS_PER_PAGE = 25;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const STATE_COLORS = {
  NOT_PROCESSED: "gray",
  PROCESSING: "yellow",
  PROCESSED: "green",
  FAILED: "red",
};

const fmtDuration = (ms) => {
  if (!ms) return "—";
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  if (h > 0) return `${h}h ${m % 60}m`;
  if (m > 0) return `${m}m ${s % 60}s`;
  return `${s}s`;
};

const shortId = (id) => (id ? id.slice(0, 8) + "…" : "—");

const safeParseTranscription = (value) => {
  if (!value || typeof value !== "string") return null;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const toTranscriptText = (transcriptionJsonString) => {
  const arr = safeParseTranscription(transcriptionJsonString);
  if (!arr || arr.length === 0) return null;
  const words = arr
    .filter((e) => e && e.type === "word" && typeof e.text === "string")
    .map((e) => e.text)
    .filter(Boolean);
  return words.length ? words.join(" ") : null;
};

/* ─── Snippet audio player ─── */
const SnippetPlayer = ({ snippetId }) => {
  const toast = useToast();
  const toastHelper = createToast(toast);

  return (
    <InlineAudioPlayer
      getBlob={async () => {
        try {
          const res = await Api.get(`/admin/audio_snippets/${snippetId}/audio`, {
            responseType: "blob",
          });
          return res.data;
        } catch {
          toastHelper.error("Failed to load audio");
          throw new Error("Failed to load audio");
        }
      }}
    />
  );
};

/* ─── Batch detail modal ─── */
const BatchDetailModal = ({ batch, isOpen, onClose }) => {
  const [snippets, setSnippets] = useState([]);
  const [snippetsLoading, setSnippetsLoading] = useState(false);
  const [snippetsPage, setSnippetsPage] = useState(1);
  const [snippetsTotal, setSnippetsTotal] = useState(0);
  const [isIngesting, setIsIngesting] = useState(false);
  const toast = useToast();
  const toastHelper = createToast(toast);
  const SNIP_PER_PAGE = 20;

  const fetchSnippetsPage = useCallback(async (page) => {
    if (!batch) return;
    try {
      const skip = (page - 1) * SNIP_PER_PAGE;
      const res = await Api.get(
        `/admin/audio_batches/${batch.id}/snippets?skip=${skip}&limit=${SNIP_PER_PAGE}`
      );
      const items = res.data.items || res.data || [];
      const total = res.data.total_count || items.length;
      return { items, total };
    } catch {
      toastHelper.error("Failed to load snippets");
      return null;
    } finally {
      // no-op
    }
  }, [batch]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSnippets = useCallback(async (page) => {
    if (!batch) return null;
    setSnippetsLoading(true);
    try {
      const result = await fetchSnippetsPage(page);
      if (result) {
        setSnippets(result.items);
        setSnippetsTotal(result.total);
      }
      return result;
    } finally {
      setSnippetsLoading(false);
    }
  }, [batch, fetchSnippetsPage]);

  useEffect(() => {
    if (isOpen && batch) { setSnippetsPage(1); loadSnippets(1); }
  }, [isOpen, batch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isOpen && batch) loadSnippets(snippetsPage);
  }, [snippetsPage]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!batch) return null;

  const handleIngest = async () => {
    if (!batch?.id || isIngesting) return;
    setIsIngesting(true);
    try {
      const before = await fetchSnippetsPage(1);
      // Prefer user-scoped endpoint; fallback to admin if present.
      try {
        await Api.post(`/audio_batches/${batch.id}/ingest`);
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401 || status === 403 || status === 404) {
          await Api.post(`/admin/audio_batches/${batch.id}/ingest`);
        } else {
          throw err;
        }
      }
      setSnippetsPage(1);

      let completed = false;
      for (let attempt = 0; attempt < 10; attempt++) {
        const res = await loadSnippets(1);
        const beforeTotal = before?.total ?? 0;
        const afterTotal = res?.total ?? 0;
        const beforeFirstId = before?.items?.[0]?.id;
        const afterFirstId = res?.items?.[0]?.id;

        if (afterTotal > 0 && (afterTotal !== beforeTotal || afterFirstId !== beforeFirstId || beforeTotal === 0)) {
          completed = true;
          break;
        }
        await sleep(1500);
      }

      if (completed) {
        toastHelper.success("Load completed");
      } else {
        toastHelper.info("Loading in progress", "Snippets may take a moment. Try again shortly.");
      }
    } catch {
      toastHelper.error("Failed to load batch");
    } finally {
      setIsIngesting(false);
    }
  };

  const MetaItem = ({ label, value }) => (
    <Box>
      <Text fontSize="xs" color="gray.400" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider" mb={0.5}>
        {label}
      </Text>
      <Text fontSize="sm" color="gray.700" fontFamily={typeof value === "string" && value.length > 20 ? "mono" : undefined}>
        {value || "—"}
      </Text>
    </Box>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="xl" boxShadow="xl" maxW="96vw">
        <ModalHeader fontSize="md" fontWeight="semibold" borderBottomWidth="1px" borderBottomColor="gray.100" pb={4} pt={6} px={7}>
          <HStack spacing={3}>
            <Text>Batch</Text>
            <Text fontFamily="mono" fontSize="sm" color="gray.400" fontWeight="normal">{batch.id}</Text>
            <Badge colorScheme={STATE_COLORS[batch.state] || "gray"} variant="subtle" borderRadius="full" px={2} fontSize="xs">
              {batch.state}
            </Badge>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody px={7} py={6}>
          <VStack spacing={6} align="stretch">
            {/* Metadata */}
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={5}>
              <MetaItem
                label="User"
                value={batch.user_email || batch.user?.email || batch.userEmail || batch.owner_email || batch.user_id}
              />
              <MetaItem label="Device" value={batch.device_id} />
              <MetaItem label="Org" value={batch.org_id} />
              <MetaItem label="Duration" value={fmtDuration(batch.total_duration_ms)} />
              <MetaItem label="Chunks" value={batch.total_chunks} />
              <MetaItem label="Sample Rate" value={batch.sample_rate ? `${batch.sample_rate} Hz` : null} />
              <MetaItem label="Encoding" value={batch.audio_encoding} />
              <MetaItem label="Created" value={formatDate(batch.recorded_at || batch.created_at)} />
              <MetaItem label="Snippets" value={snippetsTotal || batch.snippets?.length} />
            </SimpleGrid>

            <Divider />

            {/* Snippets */}
            <Box>
              <HStack justify="space-between" align="center" mb={4}>
                <Text fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">
                  Snippets
                </Text>
                <Button
                  size="xs"
                  variant="outline"
                  colorScheme="blue"
                  leftIcon={<RepeatIcon />}
                  onClick={handleIngest}
                  isLoading={isIngesting}
                  loadingText="Loading"
                >
                  Load
                </Button>
              </HStack>
              {snippetsLoading ? (
                <Flex justify="center" py={8}><Spinner size="md" color="blue.500" /></Flex>
              ) : snippets.length === 0 ? (
                <Box py={8} textAlign="center" bg="gray.50" borderRadius="lg" borderWidth="1px" borderColor="gray.200" borderStyle="dashed">
                  <Text fontSize="sm" color="gray.400">No snippets found</Text>
                </Box>
              ) : (
                <VStack spacing={3} align="stretch">
                  <Box overflowX="auto" borderRadius="lg" borderWidth="1px" borderColor="gray.200" bg="white">
                    <Table variant="unstyled" size="md">
                      <Thead>
                        <Tr bg="gray.50" borderBottomWidth="1px" borderBottomColor="gray.200">
                          {["ID", "Chunk", "Timestamp", "Duration", "Transcripts", ""].map((h) => (
                            <Th key={h} py={3} px={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">{h}</Th>
                          ))}
                        </Tr>
                      </Thead>
                      <Tbody>
                        {snippets.map((snip, idx) => {
                          const filteredText = toTranscriptText(snip.transcription_filtered);
                          const rawText = toTranscriptText(snip.transcription_unfiltered);

                          return (
                            <Tr
                              key={snip.id}
                              borderBottomWidth={idx < snippets.length - 1 ? "1px" : "0"}
                              borderBottomColor="gray.100"
                              _hover={{ bg: "gray.50" }}
                              transition="background 0.1s"
                            >
                            <Td py={3} px={4}>
                              <Text fontSize="xs" color="gray.400" fontFamily="mono">{snip.id}</Text>
                            </Td>
                            <Td py={3} px={4}>
                              <Text fontSize="xs" color="gray.600" fontFamily="mono">{snip.chunk_index ?? "—"}</Text>
                            </Td>
                              <Td py={3} px={4}>
                                <Text fontSize="xs" color="gray.500">
                                  {snip.recorded_at
                                    ? formatDate(snip.recorded_at)
                                    : snip.start_timestamp_ms
                                      ? new Date(snip.start_timestamp_ms).toLocaleString()
                                      : "—"}
                                </Text>
                              </Td>
                            <Td py={3} px={4}>
                              <Text fontSize="xs" color="gray.500">
                                {snip.end_timestamp_ms && snip.start_timestamp_ms
                                  ? fmtDuration(snip.end_timestamp_ms - snip.start_timestamp_ms)
                                  : "—"}
                              </Text>
                            </Td>
                            <Td py={3} px={4} minW="440px" maxW="720px">
                              {filteredText || rawText ? (
                                <VStack spacing={1.5} align="stretch">
                                  <HStack spacing={2} align="baseline">
                                    <Text fontSize="xs" color="gray.400" fontWeight="semibold" minW="58px">
                                      Filtered
                                    </Text>
                                    {filteredText ? (
                                      <Tooltip label={filteredText} placement="top" hasArrow>
                                        <Text fontSize="sm" color="gray.800" noOfLines={2}>
                                          {filteredText}
                                        </Text>
                                      </Tooltip>
                                    ) : (
                                      <Text fontSize="sm" color="gray.300">
                                        Pending
                                      </Text>
                                    )}
                                  </HStack>
                                  <HStack spacing={2} align="baseline">
                                    <Text fontSize="xs" color="gray.400" fontWeight="semibold" minW="58px">
                                      Raw
                                    </Text>
                                    {rawText ? (
                                      <Tooltip label={rawText} placement="top" hasArrow>
                                        <Text fontSize="xs" color="gray.600" noOfLines={2}>
                                          {rawText}
                                        </Text>
                                      </Tooltip>
                                    ) : (
                                      <Text fontSize="xs" color="gray.300">
                                        Pending
                                      </Text>
                                    )}
                                  </HStack>
                                </VStack>
                              ) : (
                                <Text fontSize="sm" color="gray.300">
                                  Pending processing
                                </Text>
                              )}
                            </Td>
                            <Td py={3} px={4} textAlign="right">
                              <SnippetPlayer snippetId={snip.id} />
                            </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </Box>
                  {snippetsTotal > SNIP_PER_PAGE && (
                    <Flex justify="flex-end">
                      <Pagination
                        currentPage={snippetsPage}
                        totalItems={snippetsTotal}
                        itemsPerPage={SNIP_PER_PAGE}
                        onPageChange={setSnippetsPage}
                        isDisabled={snippetsLoading}
                      />
                    </Flex>
                  )}
                </VStack>
              )}
            </Box>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

/* ─── Main panel ─── */
const AudioBatchManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterState, setFilterState] = usePersistentState(
    "admin:audioBatchManagement:filterState",
    ""
  );
  const [filterOrgId, setFilterOrgId] = usePersistentState(
    "admin:audioBatchManagement:filterOrgId",
    ""
  );
  // Hidden for now (keep for later).
  // const [filterDeviceId, setFilterDeviceId] = useState("");
  const [filterUserId, setFilterUserId] = usePersistentState(
    "admin:audioBatchManagement:filterUserId",
    ""
  );
  const [organizations, setOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const toastHelper = createToast(toast); // eslint-disable-line no-unused-vars

  const skip = (currentPage - 1) * ITEMS_PER_PAGE;

  const fetchBatches = useCallback(() => {
    const params = new URLSearchParams();
    if (filterState) params.append("state", filterState);
    // Hidden for now (keep for later).
    // if (filterDeviceId) params.append("device_id", filterDeviceId);
    if (filterOrgId) params.append("org_id", filterOrgId);
    if (filterUserId) params.append("user_id", filterUserId);
    params.append("skip", skip.toString());
    params.append("limit", ITEMS_PER_PAGE.toString());
    return Api.get(`/admin/audio_batches?${params.toString()}`).then((r) => r.data);
  }, [filterState, filterOrgId, filterUserId, skip]);

  const { data, loading, error } = useAdminData(fetchBatches, [filterState, filterOrgId, filterUserId, skip]);

  const batches = data?.items || [];
  const totalCount = data?.total_count || 0;

  const handleFilterState = (v) => { setFilterState(v); setCurrentPage(1); };
  const handleFilterOrg = (v) => {
    setFilterOrgId(v);
    setFilterUserId("");
    setCurrentPage(1);
  };
  // Hidden for now (keep for later).
  // const handleFilterDevice = (v) => { setFilterDeviceId(v); setCurrentPage(1); };
  const handleFilterUser = (v) => { setFilterUserId(v); setCurrentPage(1); };

  useEffect(() => {
    const urlState = searchParams.get("batch_state");
    const urlOrgId = searchParams.get("batch_org_id");
    const urlUserId = searchParams.get("batch_user_id");

    if (urlState !== null && urlState !== filterState) {
      setFilterState(urlState);
      setCurrentPage(1);
    }
    if (urlOrgId !== null && urlOrgId !== filterOrgId) {
      setFilterOrgId(urlOrgId);
      setCurrentPage(1);
    }
    if (urlUserId !== null && urlUserId !== filterUserId) {
      setFilterUserId(urlUserId);
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const before = next.toString();

    if (filterState) {
      next.set("batch_state", filterState);
    } else {
      next.delete("batch_state");
    }

    if (filterOrgId) {
      next.set("batch_org_id", filterOrgId);
    } else {
      next.delete("batch_org_id");
    }

    if (filterUserId) {
      next.set("batch_user_id", filterUserId);
    } else {
      next.delete("batch_user_id");
    }

    if (next.toString() !== before) {
      setSearchParams(next, { replace: true });
    }
  }, [filterState, filterOrgId, filterUserId, searchParams, setSearchParams]);

  const openDetail = (batch) => { setSelectedBatch(batch); onOpen(); };

  useEffect(() => {
    let mounted = true;
    Api.get("/admin/organizations")
      .then((r) => {
        if (!mounted) return;
        setOrganizations(r.data.organizations || []);
      })
      .catch(() => {
        if (!mounted) return;
        setOrganizations([]);
      });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    const query = filterOrgId ? `?organization_id=${encodeURIComponent(filterOrgId)}` : "";
    Api.get(`/admin/users${query}`)
      .then((r) => {
        if (!mounted) return;
        setUsers(r.data.users || []);
      })
      .catch(() => {
        if (!mounted) return;
        setUsers([]);
      });
    return () => { mounted = false; };
  }, [filterOrgId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <Spinner size="lg" color="blue.500" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="lg">
        <AlertIcon />
        {error?.message || "Failed to load audio batches"}
      </Alert>
    );
  }

  return (
    <>
      <VStack spacing={5} align="stretch">
        {/* Filters */}
        <Flex gap={4} wrap="wrap" align="flex-end">
          {/* Hidden for now (keep for later). */}
          {/* <FormControl maxW="280px">
            <FormLabel fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider" mb={1}>
              Device ID
            </FormLabel>
            <Input
              size="sm"
              value={filterDeviceId}
              onChange={(e) => handleFilterDevice(e.target.value)}
              placeholder="Filter by device UUID"
              fontFamily="mono"
              borderRadius="md"
              bg="white"
            />
          </FormControl> */}
          <FormControl maxW="260px">
            <FormLabel fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider" mb={1}>
              Organization
            </FormLabel>
            <Select
              size="sm"
              value={filterOrgId}
              onChange={(e) => handleFilterOrg(e.target.value)}
              placeholder="All organizations"
              borderRadius="md"
              bg="white"
            >
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </Select>
          </FormControl>
          <FormControl maxW="180px">
            <FormLabel fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider" mb={1}>
              State
            </FormLabel>
            <Select
              size="sm"
              value={filterState}
              onChange={(e) => handleFilterState(e.target.value)}
              placeholder="All states"
              borderRadius="md"
              bg="white"
            >
              <option value="NOT_PROCESSED">Not Processed</option>
              <option value="PROCESSING">Processing</option>
              <option value="PROCESSED">Processed</option>
              <option value="FAILED">Failed</option>
            </Select>
          </FormControl>
          <FormControl maxW="320px">
            <FormLabel fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider" mb={1}>
              User
            </FormLabel>
            <Select
              size="sm"
              value={filterUserId}
              onChange={(e) => handleFilterUser(e.target.value)}
              placeholder="All users"
              borderRadius="md"
              bg="white"
            >
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.email}
                </option>
              ))}
            </Select>
          </FormControl>
        </Flex>

        {/* Table */}
        {batches.length === 0 ? (
          <Box
            py={16} textAlign="center" bg="gray.50" borderRadius="lg"
            borderWidth="1px" borderColor="gray.200" borderStyle="dashed"
          >
            <Text color="gray.400" fontSize="sm">No audio batches found</Text>
          </Box>
        ) : (
          <>
            <Box overflowX="auto" borderRadius="lg" borderWidth="1px" borderColor="gray.200" bg="white">
              <Table variant="unstyled" size="sm">
                <Thead>
                  <Tr bg="gray.50" borderBottomWidth="1px" borderBottomColor="gray.200">
                    {["Batch ID", "User", "Device", "State", "Duration", "Chunks", /* "Snippets", */ "Created", ""].map((h) => (
                      <Th key={h} py={3} px={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">{h}</Th>
                    ))}
                  </Tr>
                </Thead>
                <Tbody>
                  {batches.map((batch, idx) => (
                    <Tr
                      key={batch.id}
                      borderBottomWidth={idx < batches.length - 1 ? "1px" : "0"}
                      borderBottomColor="gray.100"
                      _hover={{ bg: "gray.50" }}
                      transition="background 0.1s"
                      cursor="pointer"
                      onClick={() => openDetail(batch)}
                    >
                      <Td py={3} px={4}>
                        <Tooltip label={batch.id} placement="top" hasArrow>
                          <Text fontSize="xs" color="gray.600" fontFamily="mono" cursor="default">{shortId(batch.id)}</Text>
                        </Tooltip>
                      </Td>
                      <Td py={3} px={4} maxW="220px">
                        <Text fontSize="xs" color="gray.600" noOfLines={1}>
                          {batch.user_email || batch.user?.email || batch.userEmail || batch.owner_email || batch.user_id || "—"}
                        </Text>
                      </Td>
                      <Td py={3} px={4}>
                        <Tooltip label={batch.device_id} placement="top" hasArrow>
                          <Text fontSize="xs" color="gray.600" fontFamily="mono">{shortId(batch.device_id)}</Text>
                        </Tooltip>
                      </Td>
                      <Td py={3} px={4}>
                        <Badge
                          colorScheme={STATE_COLORS[batch.state] || "gray"}
                          variant="subtle"
                          borderRadius="full"
                          px={2}
                          fontSize="xs"
                        >
                          {batch.state?.replace("_", " ")}
                        </Badge>
                      </Td>
                      <Td py={3} px={4}>
                        <Text fontSize="xs" color="gray.600">{fmtDuration(batch.total_duration_ms)}</Text>
                      </Td>
                      <Td py={3} px={4}>
                        <Text fontSize="xs" color="gray.600">{batch.total_chunks ?? "—"}</Text>
                      </Td>
                      {/* Redundant; snippets are shown in batch detail modal. */}
                      {/* <Td py={3} px={4}>
                        <Text fontSize="xs" color="gray.600">{batch.snippets?.length ?? "—"}</Text>
                      </Td> */}
                      <Td py={3} px={4}>
                        <Text fontSize="xs" color="gray.400">{formatDate(batch.recorded_at || batch.created_at)}</Text>
                      </Td>
                      <Td py={3} px={4} textAlign="right">
                        <ChevronRightIcon color="gray.300" />
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>

            <Flex justify="space-between" align="center">
              <Text fontSize="xs" color="gray.400">
                Showing {skip + 1}–{Math.min(skip + ITEMS_PER_PAGE, totalCount)} of {totalCount} batches
              </Text>
              <Pagination
                currentPage={currentPage}
                totalItems={totalCount}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={setCurrentPage}
                isDisabled={loading}
              />
            </Flex>
          </>
        )}
      </VStack>

      <BatchDetailModal batch={selectedBatch} isOpen={isOpen} onClose={onClose} />
    </>
  );
};

export default AudioBatchManagement;
